import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { IChatListObject, IGeneratedFile } from "@frontend-shared/types";
import { EDirection, EProjectPermission } from "@frontend-shared/types";
import { isAssistantChat } from "@frontend-shared/utils";
import { useDirection } from "@radix-ui/react-direction";
import { Bot, Pin, Share2 } from "lucide-react";
import React, { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { useArchiveChat } from "@/api/chat/useArchiveChat";
import { useDeleteChat } from "@/api/chat/useDeleteChat";
import { useMoveToFolder } from "@/api/chat/useMoveToFolder";
import { usePinChat } from "@/api/chat/usePinChat";
import { useUnarchiveChat } from "@/api/chat/useUnarchiveChat";
import { useUpdateChatTitle } from "@/api/chat/useUpdateChatTitle";
import { useCreateChatFolder } from "@/api/chat-folder/useCreateChatFolder";
import { Matrix } from "@/components/icons/integrations/Matrix";
import { MicrosoftTeams } from "@/components/icons/integrations/MicrosoftTeams";
import { Slack } from "@/components/icons/integrations/Slack";
import { Telegram } from "@/components/icons/integrations/Telegram";
import { WhatsApp } from "@/components/icons/integrations/WhatsApp";
import { SidebarMenuItem } from "@/components/ui/sidebar";
import {
  SIDEBAR_ITEM_ACTIVE_CLASS,
  SIDEBAR_ITEM_HOVER_CLASS,
  SIDEBAR_ITEM_RADIUS_CLASS,
} from "@/components/ui/sidebar-item-styles";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PATHS } from "@/constants/paths";
import { useGeneratedFilesDownload } from "@/hooks/use-generated-files-download";
import { usePermissionCheck } from "@/hooks/use-permission";
import { direction, getAutoDirection } from "@/lib/direction";
import { cn } from "@/lib/utils";
import { websocketService } from "@/lib/websocket";
import { stopBackgroundTracking } from "@/lib/websocket-background-tracker";
import { ShareChatDialog } from "@/pages/chat/components/share-chat-dialog/ShareChatDialog";

import { CreateChatFolderDialog } from "../../ChatFolderList/components/CreateChatFolderDialog";
import { ArchiveChatDialog } from "./ArchiveChatDialog";
import { ChatGeneratedFilesDialog } from "./chat-generated-files-dialog/ChatGeneratedFilesDialog";
import { ChatActionMenu } from "./ChatActionMenu";
import { DeleteChatDialog } from "./DeleteChatDialog";
import { RenameChatDialog } from "./RenameChatDialog";

interface IChatListItemProps {
  chat: IChatListObject;
}

const ChatListItemInner: React.FC<IChatListItemProps> = ({ chat }) => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("sidebar");
  const [isRenameDialogOpen, setRenameDialogOpen] = useState(false);
  const [isArchiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isShareDialogOpen, setShareDialogOpen] = useState(false);
  const [isDownloadFilesDialogOpen, setDownloadFilesDialogOpen] =
    useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] =
    useState(false);

  const [hasChatShareWritePermission] = usePermissionCheck([
    EProjectPermission.CHAT_SHARE_WRITE,
  ]);
  const dir = useDirection();

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: chat.uuid,
      data: {
        type: "chat",
        chatUuid: chat.uuid,
        chatTitle: chat.title,
        isArchived: chat.isArchived,
      },
    });
  const dragStyle = transform
    ? {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.3 : undefined,
      }
    : undefined;

  const { mutate: updateTitle } = useUpdateChatTitle();
  const { mutate: archiveChat } = useArchiveChat(() => {
    if (chatId === chat.uuid) {
      navigate(PATHS.CHAT);
    }
  });
  const { mutate: deleteChat } = useDeleteChat(
    (deletedChatUuid) => {
      // NOTE: Leave WebSocket room and stop background tracking to prevent stale events.
      stopBackgroundTracking(deletedChatUuid);
      websocketService.leaveChat(deletedChatUuid);

      if (chatId === chat.uuid) {
        navigate(PATHS.CHAT);
      }
      toast.success(t("chat_list.delete_chat.success"));
    },
    () => {
      toast.error(t("chat_list.delete_chat.error"));
    },
  );
  const { mutate: unarchiveChat } = useUnarchiveChat();
  const { mutate: pinChat } = usePinChat();
  const { mutate: moveToFolder } = useMoveToFolder();
  const { mutateAsync: createFolder } = useCreateChatFolder();

  const handleRename = useCallback(
    (newTitle: string): void => {
      updateTitle({ uuid: chat.uuid, title: newTitle });
    },
    [updateTitle, chat.uuid],
  );

  const handleArchive = useCallback((): void => {
    archiveChat(chat.uuid);
  }, [archiveChat, chat.uuid]);

  const handleDelete = useCallback((): void => {
    deleteChat(chat.uuid);
  }, [deleteChat, chat.uuid]);

  const handleUnarchive = useCallback((): void => {
    unarchiveChat({ uuid: chat.uuid });
  }, [unarchiveChat, chat.uuid]);

  const handlePin = useCallback((): void => {
    pinChat(chat.uuid);
  }, [pinChat, chat.uuid]);

  const handleMoveToFolder = useCallback(
    (folderUuid: string | null): void => {
      moveToFolder({ chatUuid: chat.uuid, folderId: folderUuid });
      setIsMenuOpen(false);
    },
    [moveToFolder, chat.uuid],
  );

  const handleCreateFolderAndMove = useCallback((): void => {
    setIsCreateFolderDialogOpen(true);
    setIsMenuOpen(false);
  }, []);

  const handleCreateFolderAndMoveChat = useCallback(
    async (name: string): Promise<void> => {
      const newFolder = await createFolder({ name });
      moveToFolder({ chatUuid: chat.uuid, folderId: newFolder.uuid });
    },
    [createFolder, moveToFolder, chat.uuid],
  );

  const onRenameClick = useCallback((): void => {
    setRenameDialogOpen(true);
    setIsMenuOpen(false);
  }, []);

  const onArchiveClick = useCallback((): void => {
    if (chat.isArchived) {
      // NOTE: Unarchive directly without confirmation dialog
      handleUnarchive();
      setIsMenuOpen(false);
    } else {
      setArchiveDialogOpen(true);
      setIsMenuOpen(false);
    }
  }, [chat.isArchived, handleUnarchive]);

  const onDeleteClick = useCallback((): void => {
    setDeleteDialogOpen(true);
    setIsMenuOpen(false);
  }, []);

  const onShareClick = useCallback((): void => {
    setShareDialogOpen(true);
    setIsMenuOpen(false);
  }, []);

  // NOTE: NSAI-5312 — the dialog closes as the download starts, so its
  // progress lives in a toast, and closing that toast cancels the download.
  const {
    downloadFiles,
    isDownloading: isFilesDownloading,
    progressLabel: filesProgressLabel,
  } = useGeneratedFilesDownload({ shouldShowProgressToast: true });

  const onDownloadFilesClick = useCallback((): void => {
    setDownloadFilesDialogOpen(true);
    setIsMenuOpen(false);
  }, []);

  const handleDownloadFiles = useCallback(
    (files: readonly IGeneratedFile[]): void => {
      setDownloadFilesDialogOpen(false);
      void downloadFiles(files);
    },
    [downloadFiles],
  );

  const isRtl = dir === "rtl";

  // NOTE: NSAI-5893 — the fade has to follow the TITLE's direction, not the
  // app's. An LTR title inside an RTL sidebar is laid out from the right edge
  // and overflows past its own start, so a locale-driven mask erases the first
  // characters instead of the last. The hover padding still follows the app
  // direction, because that is the side the action menu opens on.
  const titleDirection = direction(chat.title);
  const isTitleRtl = titleDirection === EDirection.Rtl;

  return (
    <div ref={setNodeRef} style={dragStyle} {...attributes} {...listeners}>
      <SidebarMenuItem data-testid={`sidebar-chat-list-item-${chat.uuid}`}>
        <NavLink
          to={`/chat/${chat.uuid}`}
          state={{ isNewChat: false }}
          data-testid="chat-list-item-link"
          // NOTE: NSAI-6259 — the active row gains a left accent bar on top
          // of its fill. A tinted pill alone is easy to lose in a long list,
          // especially once a hovered row a few lines away is wearing a very
          // similar one; the bar is the thing the eye finds. `start-*` rather
          // than `left-*` so it stays on the leading edge in RTL.
          //
          // NOTE: NSAI-6259 follow-up — these are the SAME four values the
          // sidebar's own menu button uses (`sidebarMenuButtonVariants`:
          // `inset-y-1.5 start-0 w-0.5 rounded-full`), deliberately, because
          // the review asked for this marker to look like that one. An attempt
          // to inset and thin it (`start-1 w-px`) went the wrong way: it no
          // longer reached the leading edge of the row and was too faint to
          // find, which is the opposite of what the marker is for. Keep the two
          // in step — if the sidebar's changes, this changes with it.
          className={({ isActive }) =>
            cn(
              "group/menu-item block relative w-full px-3 py-2 text-xs [--menu-space:2.5rem] hover:[--menu-space:4rem] max-w-full",
              SIDEBAR_ITEM_RADIUS_CLASS,
              isActive ? SIDEBAR_ITEM_ACTIVE_CLASS : SIDEBAR_ITEM_HOVER_CLASS,
            )
          }
        >
          {() => (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p
                      dir={titleDirection}
                      className={cn(
                        "overflow-hidden whitespace-nowrap transition-all duration-150",
                        isRtl
                          ? "group-hover/menu-item:pl-10"
                          : "group-hover/menu-item:pr-10",
                        isTitleRtl
                          ? "chat-mask-fade-left"
                          : "chat-mask-fade-right",
                        isMenuOpen && (isRtl ? "pl-10" : "pr-10"),
                      )}
                    >
                      {chat.isPinned && !chat.isArchived && (
                        <Pin
                          className="inline-block size-3 me-1 text-muted-foreground shrink-0 fill-current"
                          data-testid="chat-list-item-pin-indicator"
                        />
                      )}
                      {isAssistantChat(chat) && (
                        <Bot
                          className="inline-block size-3 me-1 text-muted-foreground shrink-0"
                          data-testid="chat-list-item-assistant-indicator"
                        />
                      )}
                      {chat.telegramChatId && (
                        <Telegram
                          className="inline-block size-3 me-1 text-muted-foreground shrink-0"
                          data-testid="chat-list-item-telegram-indicator"
                        />
                      )}
                      {chat.slackChannelId && (
                        <Slack
                          className="inline-block size-3 me-1 shrink-0"
                          data-testid="chat-list-item-slack-indicator"
                        />
                      )}
                      {chat.teamsConversationId && (
                        <MicrosoftTeams
                          className="inline-block size-3 me-1 shrink-0"
                          data-testid="chat-list-item-teams-indicator"
                        />
                      )}
                      {chat.isWhatsappChat && (
                        <WhatsApp
                          className="inline-block size-3 me-1 shrink-0 mb-0.5"
                          data-testid="chat-list-item-whatsapp-indicator"
                        />
                      )}
                      {chat.isMatrixChat && (
                        <Matrix
                          className="inline-block size-3 me-1 shrink-0 mb-0.5"
                          data-testid="chat-list-item-matrix-indicator"
                        />
                      )}
                      {chat.hasActiveShare && (
                        <Share2
                          className="inline-block size-3 me-1 text-muted-foreground shrink-0"
                          data-testid="chat-list-item-share-indicator"
                        />
                      )}
                      <span dir={getAutoDirection()}>{chat.title}</span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent
                    data-testid="chat-title-tooltip"
                    className="max-w-sm break-words"
                  >
                    <p>{chat.title}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <ChatActionMenu
                isMenuOpen={isMenuOpen}
                onOpenChange={setIsMenuOpen}
                onRenameClick={onRenameClick}
                onShareClick={onShareClick}
                onDownloadFilesClick={onDownloadFilesClick}
                onArchiveClick={onArchiveClick}
                onDeleteClick={onDeleteClick}
                onPinClick={handlePin}
                onMoveToFolder={handleMoveToFolder}
                onCreateFolderAndMove={handleCreateFolderAndMove}
                isArchived={chat.isArchived}
                isPinned={chat.isPinned}
                currentFolderId={chat.folderId}
                hasChatShareWritePermission={hasChatShareWritePermission}
                hasActiveShare={chat.hasActiveShare}
              />
            </>
          )}
        </NavLink>
        <RenameChatDialog
          chat={chat}
          isOpen={isRenameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          onRename={handleRename}
        />
        <ArchiveChatDialog
          chat={chat}
          isOpen={isArchiveDialogOpen}
          onOpenChange={setArchiveDialogOpen}
          onArchive={handleArchive}
        />
        <DeleteChatDialog
          chat={chat}
          isOpen={isDeleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDelete={handleDelete}
        />
        <CreateChatFolderDialog
          isOpen={isCreateFolderDialogOpen}
          onOpenChange={setIsCreateFolderDialogOpen}
          onCreate={handleCreateFolderAndMoveChat}
        />
        <ChatGeneratedFilesDialog
          chatUuid={chat.uuid}
          isOpen={isDownloadFilesDialogOpen}
          onOpenChange={setDownloadFilesDialogOpen}
          isDownloading={isFilesDownloading}
          progressLabel={filesProgressLabel}
          onDownload={handleDownloadFiles}
        />
        {hasChatShareWritePermission && (
          <ShareChatDialog
            open={isShareDialogOpen}
            chatUuid={chat.uuid}
            onOpenChange={setShareDialogOpen}
          />
        )}
      </SidebarMenuItem>
    </div>
  );
};

export const ChatListItem = memo(ChatListItemInner);
